import { Button } from '@/components/ui/Button';
import { SurveyTemplate, SurveyQuestion } from '@/types/database';
import { Plus, Trash2, GripVertical, Loader2 } from 'lucide-react';

interface SurveyTemplateWithQuestions extends SurveyTemplate {
  questions: SurveyQuestion[];
}

interface FormData {
  title: string;
  description: string;
  questions: {
    id?: string;
    text: string;
    type: 'multiple-choice' | 'open-ended' | 'multi-select';
    options: string[];
    order_index: number;
  }[];
}

interface SurveyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  formData: FormData;
  setFormData: (data: FormData) => void;
  editingTemplate: SurveyTemplateWithQuestions | null;
  saving: boolean;
}

export function SurveyFormModal({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  editingTemplate,
  saving
}: SurveyFormModalProps) {
  if (!isOpen) return null;

  const handleAddQuestion = () => {
    setFormData({
      ...formData,
      questions: [
        ...formData.questions,
        {
          text: '',
          type: 'multiple-choice',
          options: [''],
          order_index: formData.questions.length
        }
      ]
    });
  };

  const handleRemoveQuestion = (index: number) => {
    setFormData({
      ...formData,
      questions: formData.questions.filter((_, i) => i !== index)
    });
  };

  const handleAddOption = (questionIndex: number) => {
    setFormData({
      ...formData,
      questions: formData.questions.map((q, i) => 
        i === questionIndex
          ? { ...q, options: [...(q.options || []), ''] }
          : q
      )
    });
  };

  const handleRemoveOption = (questionIndex: number, optionIndex: number) => {
    setFormData({
      ...formData,
      questions: formData.questions.map((q, i) => 
        i === questionIndex
          ? { ...q, options: q.options?.filter((_, j) => j !== optionIndex) }
          : q
      )
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl my-8">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {editingTemplate ? 'Edit Survey Template' : 'Create Survey Template'}
          </h2>
        </div>

        <form onSubmit={onSubmit}>
          <div className="px-6 py-4 max-h-[calc(100vh-16rem)] overflow-y-auto">
            <div className="space-y-4">
              {/* Form fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Title
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Questions Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Questions</h3>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddQuestion}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Question
                  </Button>
                </div>

                <div className="space-y-4">
                  {formData.questions.map((question, questionIndex) => (
                    <div
                      key={questionIndex}
                      className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4"
                    >
                      <div className="flex items-start gap-4">
                        <GripVertical className="h-5 w-5 text-gray-400 dark:text-gray-500 cursor-move flex-shrink-0 mt-2" />
                        <div className="flex-1 min-w-0 space-y-4">
                          {/* Question fields */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Question Text
                            </label>
                            <input
                              type="text"
                              required
                              value={question.text}
                              onChange={(e) => setFormData({
                                ...formData,
                                questions: formData.questions.map((q, i) =>
                                  i === questionIndex
                                    ? { ...q, text: e.target.value }
                                    : q
                                )
                              })}
                              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Question Type
                            </label>
                            <select
                              value={question.type}
                              onChange={(e) => setFormData({
                                ...formData,
                                questions: formData.questions.map((q, i) =>
                                  i === questionIndex
                                    ? { ...q, type: e.target.value as 'multiple-choice' | 'open-ended' | 'multi-select' }
                                    : q
                                )
                              })}
                              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                              <option value="multiple-choice">Multiple Choice</option>
                              <option value="multi-select">Multi-Select (checkboxes)</option>
                              <option value="open-ended">Open Ended</option>
                            </select>
                          </div>

                          {(question.type === 'multiple-choice' || question.type === 'multi-select') && (
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Options
                              </label>
                              {question.options?.map((option, optionIndex) => (
                                <div key={optionIndex} className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    required
                                    value={option}
                                    onChange={(e) => setFormData({
                                      ...formData,
                                      questions: formData.questions.map((q, i) =>
                                        i === questionIndex
                                          ? {
                                              ...q,
                                              options: q.options?.map((opt, j) =>
                                                j === optionIndex ? e.target.value : opt
                                              )
                                            }
                                          : q
                                      )
                                    })}
                                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder={`Option ${optionIndex + 1}`}
                                  />
                                  {question.options.length > 1 && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleRemoveOption(questionIndex, optionIndex)}
                                      className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex-shrink-0"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleAddOption(questionIndex)}
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Option
                              </Button>
                            </div>
                          )}
                        </div>

                        {formData.questions.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleRemoveQuestion(questionIndex)}
                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex-shrink-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}